import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../useUser';

interface WorkerDashboardMetrics {
  jobCards: {
    total: number;
    active: number;
    completed: number;
    upcoming: number;
  };
  assignedTasks: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
  };
  workOrders: {
    total: number;
    active: number;
  };
  currentJobCard: {
    id: string;
    operation: string;
    startTime: string;
    timeSpent: number;
  } | null;
  recentActivities: Array<{
    id: string | number;
    title: string;
    description: string;
    timestamp: string;
    type: string;
  }>;
}

interface UseWorkerDashboardMetricsResult {
  metrics: WorkerDashboardMetrics | null;
  isLoading: boolean;
  error: Error | null;
}

export const useWorkerDashboardMetrics = (): UseWorkerDashboardMetricsResult => {
  const { user } = useUser();
  const [metrics, setMetrics] = useState<WorkerDashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchWorkerMetrics = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Initialize variables to hold query results
        let jobCardsData = null;
        let activeJobCardData = null;
        let activitiesData = null;
        
        try {
          // Example of fetching job cards assigned to this worker
          const { data: jcData, error: jobCardsError } = await supabase
            .from('job_cards')
            .select('*')
            .eq('assigned_worker_id', user.id)
            .eq('is_deleted', false);

          if (!jobCardsError) {
            jobCardsData = jcData;
          }

          // Get current active job card if any - note the capitalized status
          const { data: activeJC, error: activeJobCardError } = await supabase
            .from('job_cards')
            .select('id, operation_name, actual_start_time, elapsed_time_minutes')
            .eq('assigned_worker_id', user.id)
            .eq('status', 'In Progress')  // Corrected case and format
            .limit(1);

          if (!activeJobCardError && activeJC && activeJC.length > 0) {
            activeJobCardData = activeJC[0];
          }

          // Example of fetching recent activities
          const { data: activities, error: activitiesError } = await supabase
            .from('worker_activities')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false })
            .limit(10);

          if (!activitiesError) {
            activitiesData = activities;
          }
        } catch (error) {
          // Silent graceful fallback to mock data
        }

        // Calculate metrics or use fallbacks
        const jobCardMetrics = {
          total: jobCardsData?.length || 15,
          active: jobCardsData?.filter(jc => jc.status === 'In Progress')?.length || 3,
          completed: jobCardsData?.filter(jc => jc.status === 'Completed')?.length || 10,
          upcoming: jobCardsData?.filter(jc => jc.status === 'Assigned')?.length || 2,
        };

        // Build the metrics object
        const mockMetrics: WorkerDashboardMetrics = {
          jobCards: jobCardMetrics,
          assignedTasks: {
            total: 12,
            pending: 4,
            inProgress: 3,
            completed: 5,
          },
          workOrders: {
            total: 8,
            active: 3,
          },
          currentJobCard: activeJobCardData ? {
            id: activeJobCardData.id,
            operation: activeJobCardData.operation_name,
            startTime: activeJobCardData.actual_start_time,
            timeSpent: activeJobCardData.elapsed_time_minutes || 0,
          } : null,
          recentActivities: activitiesData ? activitiesData.map(activity => ({
            id: activity.id,
            title: activity.title,
            description: activity.description,
            timestamp: activity.created_at,
            type: activity.type,
          })) : [
            {
              id: '1',
              title: 'Job Card Completed',
              description: 'You completed Job Card #JC-2523-0542',
              timestamp: new Date(Date.now() - 3600000).toISOString(),
              type: 'jobCard',
            },
            {
              id: '2',
              title: 'Task Assigned',
              description: 'You were assigned to Work Order #WO-2523-0123',
              timestamp: new Date(Date.now() - 86400000).toISOString(),
              type: 'workOrder',
            },
            {
              id: '3',
              title: 'Issue Reported',
              description: 'You reported an issue with machine M-1024',
              timestamp: new Date(Date.now() - 172800000).toISOString(),
              type: 'task',
            }
          ],
        };

        setMetrics(mockMetrics);
      } catch (err) {
        // Use console.error instead of console.log for errors
        console.error('Error fetching worker dashboard metrics:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkerMetrics();
  }, [user]);

  return { metrics, isLoading, error };
};
